
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMemo } from 'react';

interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: string;
}

const routeLabels: Record<string, { label: string; icon: string; parent?: string }> = {
  '/dashboard': { label: 'Tableau de bord', icon: 'ri-dashboard-line' },
  '/dossiers': { label: 'Dossiers', icon: 'ri-folder-3-line' },
  '/dossiers/nouveau': { label: 'Nouveau Dossier', icon: 'ri-add-line', parent: '/dossiers' },
  '/nouveau-dossier': { label: 'Nouveau Dossier', icon: 'ri-add-line', parent: '/dossiers' },
  '/demandes': { label: 'Réquissitions', icon: 'ri-file-list-3-line' },
  '/parties': { label: 'Parties', icon: 'ri-group-line' },
  '/rapports-dissemination': { label: 'Rapports de Dissémination', icon: 'ri-file-text-line' },
  '/rapports-dissemination/nouveau': { label: 'Nouveau Rapport', icon: 'ri-add-line', parent: '/rapports-dissemination' },
  '/echanges-inter-organisations': { label: 'Demandes d\'Information', icon: 'ri-exchange-line' },
  '/declarations-systematiques': { label: 'Déclarations Systématiques', icon: 'ri-file-chart-line' },
  '/suivi-delais-72h': { label: 'Suivi Délais 72h', icon: 'ri-alarm-warning-line' },
  '/suivi-reponses': { label: 'Suivi des Réponses', icon: 'ri-reply-line' },
  '/gestion-documentaire': { label: 'Gestion Documentaire', icon: 'ri-folder-open-line' },
  '/reporting': { label: 'Reporting', icon: 'ri-bar-chart-box-line' },
  '/statistiques-avancees': { label: 'Statistiques Avancées', icon: 'ri-bar-chart-2-line', parent: '/reporting' },
  '/statistiques-delais': { label: 'Statistiques des Délais', icon: 'ri-timer-line', parent: '/reporting' },
  '/stats-type-organisation': { label: 'Stats par Type d\'Organisation', icon: 'ri-pie-chart-line', parent: '/reporting' },
  '/conformite-organisations': { label: 'Conformité par Organisation', icon: 'ri-shield-check-line', parent: '/reporting' },
  '/notifications-alertes': { label: 'Notifications & Alertes', icon: 'ri-notification-3-line' },
  '/operation-centif': { label: 'Opérations CENTIF', icon: 'ri-database-2-line' },
  '/recherche-avancee': { label: 'Recherche Avancée', icon: 'ri-search-2-line' },
  '/gestion-utilisateurs': { label: 'Gestion des Utilisateurs', icon: 'ri-group-line' },
  '/gestion-organisations': { label: 'Gestion des Organisations', icon: 'ri-building-2-line' },
  '/administration': { label: 'Administration', icon: 'ri-settings-3-line' },
  '/parametres': { label: 'Paramètres Système', icon: 'ri-equalizer-line' },
  '/mon-organisme': { label: 'Mon Organisme', icon: 'ri-building-2-line' },
  '/profil': { label: 'Mon Profil', icon: 'ri-user-line' },
  '/echange-inter-organisations': { label: 'Demandes d\'Information', icon: 'ri-exchange-line' },
  '/espace-collaborateur': { label: 'Espace Collaborateur', icon: 'ri-global-line' },
  '/espace-enqueteur': { label: 'Espace Enquêteur', icon: 'ri-shield-user-line' },
  '/espace-lecteur': { label: 'Espace Lecteur', icon: 'ri-eye-line' },
  '/mon-espace': { label: 'Mon Espace', icon: 'ri-user-heart-line' },
  '/superviseur': { label: 'Tableau de bord Superviseur', icon: 'ri-team-line' },
};

export default function Breadcrumb() {
  const location = useLocation();
  const params = useParams();
  const pathname = location.pathname;

  const breadcrumbs = useMemo(() => {
    const items: BreadcrumbItem[] = [
      { label: 'Accueil', path: '/dashboard', icon: 'ri-home-4-line' },
    ];

    // Vérifier correspondance exacte
    const exactMatch = routeLabels[pathname];
    if (exactMatch) {
      // Ajouter le parent s'il existe
      if (exactMatch.parent) {
        const parentRoute = routeLabels[exactMatch.parent];
        if (parentRoute) {
          items.push({
            label: parentRoute.label,
            path: exactMatch.parent,
            icon: parentRoute.icon,
          });
        }
      }
      items.push({ label: exactMatch.label, icon: exactMatch.icon });
      return items;
    }

    // Gérer les routes dynamiques avec paramètres
    // /dossiers/:id
    if (pathname.startsWith('/dossiers/') && params.id) {
      items.push({
        label: 'Gestion des Dossiers',
        path: '/dossiers',
        icon: 'ri-folder-line',
      });
      items.push({
        label: `Dossier #${params.id.substring(0, 8)}`,
        icon: 'ri-file-text-line',
      });
      return items;
    }

    // /demande/:id
    if (pathname.startsWith('/demande/') && params.id) {
      items.push({
        label: 'Demandes d\'Information',
        path: '/demandes',
        icon: 'ri-mail-send-line',
      });
      items.push({
        label: `Demande #${params.id.substring(0, 8)}`,
        icon: 'ri-mail-open-line',
      });
      return items;
    }

    // Fallback : construire à partir des segments d'URL
    const segments = pathname.split('/').filter(Boolean);
    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const route = routeLabels[currentPath];
      if (route) {
        if (index < segments.length - 1) {
          items.push({ label: route.label, path: currentPath, icon: route.icon });
        } else {
          items.push({ label: route.label, icon: route.icon });
        }
      }
    });

    return items;
  }, [pathname, params]);

  if (breadcrumbs.length <= 1) return null;

  return (
    <nav aria-label="Fil d&apos;Ariane" className="flex items-center">
      <ol className="flex items-center space-x-1.5 text-sm">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const isFirst = index === 0;

          return (
            <li key={`${item.label}-${index}`} className="flex items-center">
              {index > 0 && (
                <div className="w-4 h-4 flex items-center justify-center text-gray-300 mr-1.5">
                  <i className="ri-arrow-right-s-line text-sm"></i>
                </div>
              )}
              {isLast ? (
                <span className="flex items-center gap-1.5 text-emerald-700 font-medium bg-emerald-50 px-2.5 py-1 rounded-md">
                  {item.icon && (
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${item.icon} text-xs`}></i>
                    </div>
                  )}
                  <span className="max-w-[200px] truncate">{item.label}</span>
                </span>
              ) : (
                <Link
                  to={item.path || '/dashboard'}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors cursor-pointer ${
                    isFirst
                      ? 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  {item.icon && (
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${item.icon} text-xs`}></i>
                    </div>
                  )}
                  <span className="max-w-[180px] truncate">{item.label}</span>
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
